import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Zap, TrendingDown, CheckCircle, X, ArrowRight, Sparkles, ShoppingBag, DollarSign } from 'lucide-react';
export function ComparisonArticlePage() {
  useEffect(() => {
    // SEO meta tags
    document.title = 'ShoppingExpert.ai vs ChatGPT: Which One Is Better for Product Decisions? | 2025 Comparison';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "ChatGPT is powerful, but is it the best tool for shopping and product decisions? Learn how ShoppingExpert.ai's multi-agent system compares in 2025.");
    }
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-slate-50" data-id="element-577">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-200" data-id="element-578">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4" data-id="element-579">
          <div className="flex items-center justify-between" data-id="element-580">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-581">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yJ3FscyPfrN1xAm7crLNW/ChatGPT_Image_Dec_2%2C_2025%2C_10_42_07_PM.png" alt="Shopping Expert.ai Logo" className="h-12 object-contain" data-id="element-582" />
              <span className="text-xl font-bold text-slate-900" data-id="element-583">
                AI Shopping Expert
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm" data-id="element-584">
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-585">
                Home
              </Link>
              <Link to="/research" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-586">
                Research Tools
              </Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-587">
                About
              </Link>
              <Link to="/contact" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-588">
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-12" data-id="element-589">
        <Breadcrumbs items={[{
        label: 'Blog',
        href: '/blog'
      }, {
        label: 'ShoppingExpert.ai vs ChatGPT',
        href: '/shoppingexpert-vs-chatgpt'
      }]} data-id="element-590" />

        {/* Article Header */}
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12" data-id="element-591">
          <div className="mb-8" data-id="element-592">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold mb-4" data-id="element-593">
              <Sparkles size={16} data-id="element-594" />
              Comparison Guide
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-595">
              ShoppingExpert.ai vs ChatGPT: Which One Is Better for Product
              Decisions?
            </h1>
            <p className="text-lg text-slate-600" data-id="element-596">
              ChatGPT is great for general questions — but is it the best tool
              for product research and buying decisions? This guide compares
              ChatGPT to ShoppingExpert.ai, a multi-agent system built for smart
              shopping in 2025.
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate max-w-none mb-12" data-id="element-597">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-598">
              Introduction
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-599">
              ChatGPT changed everything. It became the world's most popular AI
              tool for learning, summarizing, and solving everyday problems.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-600">
              But when it comes to product research, price comparison, and
              shopping decisions, the question is different:
            </p>
            <p className="text-xl text-slate-900 leading-relaxed mb-4 font-semibold" data-id="element-601">
              Should you use ChatGPT — or a specialized AI like
              ShoppingExpert.ai?
            </p>
            <p className="text-lg text-slate-700 leading-relaxed" data-id="element-602">
              In this guide, we compare both tools and explain why
              ShoppingExpert.ai was built specifically to solve the problems
              ChatGPT can't. Learn more about{' '}
              <Link to="/what-is-ai-shopping-assistant" className="text-purple-600 hover:text-purple-700 underline" data-id="element-603">
                what AI shopping assistants are
              </Link>{' '}
              and how they work.
            </p>
          </div>

          {/* ChatGPT Section */}
          <div className="mb-12" data-id="element-604">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-605">
              🤖 ChatGPT: Amazing General AI — Limited for Shopping
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-606">
              ChatGPT is powerful, but it's designed for general intelligence,
              not specialized shopping tasks.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-607">
              {/* What ChatGPT Does Well */}
              <div className="p-6 rounded-xl bg-green-50 border-2 border-green-200" data-id="element-608">
                <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2" data-id="element-609">
                  <CheckCircle size={24} className="text-green-600" data-id="element-610" />
                  What ChatGPT does well:
                </h3>
                <ul className="space-y-2" data-id="element-611">
                  {['Summaries', 'General explanations', 'Brainstorming', 'Conversations', 'Simple comparisons', 'Information you already know'].map((item, idx) => <li key={idx} className="flex items-center gap-2 text-slate-700" data-id="element-612">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0" data-id="element-613" />
                      <span data-id="element-614">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* What ChatGPT Doesn't Do Well */}
              <div className="p-6 rounded-xl bg-red-50 border-2 border-red-200" data-id="element-615">
                <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2" data-id="element-616">
                  <X size={24} className="text-red-600" data-id="element-617" />
                  What ChatGPT does NOT do well for shopping:
                </h3>
                <ul className="space-y-2" data-id="element-618">
                  {['Real-time price comparison', 'Cross-store analysis', 'Coupon detection', 'Deep product review mining', 'Identifying hidden issues', 'Import tax calculations', 'Final price breakdown', 'Multi-model elimination', 'Seasonal shopping strategy'].map((item, idx) => <li key={idx} className="flex items-center gap-2 text-slate-700" data-id="element-619">
                      <X size={16} className="text-red-600 flex-shrink-0" data-id="element-620" />
                      <span data-id="element-621">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <p className="text-lg text-slate-700 leading-relaxed font-semibold" data-id="element-622">
              ChatGPT is like a very smart friend — but it's not trained to be
              your personal shopping researcher.
            </p>
          </div>

          {/* ShoppingExpert.ai Section */}
          <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200" data-id="element-623">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-624">
              <Zap className="text-purple-600" size={32} data-id="element-625" />
              🧠 ShoppingExpert.ai: Purpose-Built for Smart Buying Decisions
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-626">
              ShoppingExpert.ai is different because it's{' '}
              <strong data-id="element-627">specialized</strong>.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-628">
              It uses a network of dedicated AI agents, each trained for a
              specific shopping domain:
            </p>

            <div className="space-y-3" data-id="element-629">
              {[{
              icon: '💻',
              title: 'Electronics AI Expert',
              desc: 'Finds the best TVs, laptops, headphones, monitors.',
              link: '/?category=general'
            }, {
              icon: '👔',
              title: 'Fashion AI Stylist',
              desc: 'Helps with fit, style, materials, and product alternatives.',
              link: '/?category=fashion'
            }, {
              icon: '🏠',
              title: 'Home & Kitchen AI Advisor',
              desc: 'Evaluates build quality, durability, and user complaints.',
              link: '/?category=home'
            }, {
              icon: '🎟️',
              title: 'AI Coupon Finder',
              desc: 'Discovers hidden codes and applies stacking strategies.',
              link: '/?category=shopping'
            }, {
              icon: '💰',
              title: 'AI Price Comparison Agent',
              desc: 'Checks Amazon, BestBuy, Walmart, Target, and more.',
              link: '/?category=shopping'
            }, {
              icon: '🌍',
              title: 'AI Import & Tax Calculator',
              desc: 'Tells you the real final price before you buy.',
              link: '/?category=shopping'
            }, {
              icon: '📊',
              title: 'AI Deal Tracker',
              desc: 'Alerts you when prices drop or when "not to buy."',
              link: '/?category=shopping'
            }].map((agent, idx) => <div key={idx} className="p-4 rounded-xl bg-white border border-purple-200 hover:border-purple-300 transition-colors" data-id="element-630">
                  <div className="flex items-start gap-3" data-id="element-631">
                    <span className="text-2xl flex-shrink-0" data-id="element-632">{agent.icon}</span>
                    <div data-id="element-633">
                      <h3 className="font-bold text-lg text-purple-900 mb-1" data-id="element-634">
                        {agent.title}
                      </h3>
                      <p className="text-slate-700 text-sm" data-id="element-635">{agent.desc}</p>
                    </div>
                  </div>
                </div>)}
            </div>

            <p className="text-lg text-slate-900 leading-relaxed mt-6 font-bold text-center" data-id="element-636">
              ChatGPT cannot do these.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="mb-12" data-id="element-637">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-638">
              🔎 Side-by-Side Comparison Table
            </h2>
            <div className="overflow-x-auto" data-id="element-639">
              <table className="w-full border-collapse" data-id="element-640">
                <thead data-id="element-641">
                  <tr className="bg-slate-100" data-id="element-642">
                    <th className="border border-slate-300 p-4 text-left font-bold" data-id="element-643">
                      Feature
                    </th>
                    <th className="border border-slate-300 p-4 text-center font-bold" data-id="element-644">
                      ChatGPT
                    </th>
                    <th className="border border-slate-300 p-4 text-center font-bold bg-purple-50" data-id="element-645">
                      ShoppingExpert.ai
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-646">
                  {[{
                  feature: 'Real-time price comparison',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Multi-agent system',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Review mining & problem detection',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Coupon discovery',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Best alternative finder',
                  chatgpt: 'partial',
                  shopping: true
                }, {
                  feature: 'Import tax calculation',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Personalized pre-research workflow',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Dedicated electronics expert',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Works on WhatsApp (soon)',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'U.S. shopping specialization',
                  chatgpt: false,
                  shopping: true
                }].map((row, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-647">
                      <td className="border border-slate-300 p-4 font-medium" data-id="element-648">
                        {row.feature}
                      </td>
                      <td className="border border-slate-300 p-4 text-center" data-id="element-649">
                        {row.chatgpt === true ? <CheckCircle size={24} className="text-green-600 mx-auto" data-id="element-650" /> : row.chatgpt === 'partial' ? <span className="text-yellow-600 font-semibold" data-id="element-651">
                            ⚠️ Partial
                          </span> : <X size={24} className="text-red-600 mx-auto" data-id="element-652" />}
                      </td>
                      <td className="border border-slate-300 p-4 text-center bg-purple-50" data-id="element-653">
                        {row.shopping ? <CheckCircle size={24} className="text-green-600 mx-auto" data-id="element-654" /> : <X size={24} className="text-red-600 mx-auto" data-id="element-655" />}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pre-Research Workflow */}
          <div className="mb-12" data-id="element-656">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-657">
              🔬 The Real Difference: Pre-Research Workflow
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-658">
              ShoppingExpert.ai runs a full research pipeline, not just a text
              answer:
            </p>

            <div className="space-y-4" data-id="element-659">
              {[{
              step: '1',
              title: 'Understand needs',
              desc: 'Usage, lifestyle, budget, preferences.'
            }, {
              step: '2',
              title: 'Shortlist products',
              desc: 'AI eliminates overpriced or low-quality models.'
            }, {
              step: '3',
              title: 'Compare real problems',
              desc: 'Warranty issues, common failures, battery decay, noise levels, defects.'
            }, {
              step: '4',
              title: 'Analyze price history',
              desc: 'Avoid bad times to buy.'
            }, {
              step: '5',
              title: 'Check coupons',
              desc: 'Stack promo codes + cashback.'
            }, {
              step: '6',
              title: 'Calculate final price',
              desc: 'Including shipping & import tax.'
            }, {
              step: '7',
              title: 'Give a final recommendation',
              desc: 'Clear, simple, personalized.'
            }].map((item, idx) => <div key={idx} className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200" data-id="element-660">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center" data-id="element-661">
                    {item.step}
                  </div>
                  <div data-id="element-662">
                    <h3 className="font-bold text-lg text-slate-900 mb-1" data-id="element-663">
                      {item.title}
                    </h3>
                    <p className="text-slate-700" data-id="element-664">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-6 p-6 rounded-xl bg-blue-50 border-2 border-blue-200" data-id="element-665">
              <p className="text-lg font-bold text-blue-900 text-center" data-id="element-666">
                ChatGPT doesn't do workflow-based research. ShoppingExpert.ai
                does this automatically.
              </p>
            </div>
          </div>

          {/* Money Savings */}
          <div className="mb-12" data-id="element-667">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-668">
              <DollarSign className="text-green-600" size={32} data-id="element-669" />
              💰 Which One Saves More Money?
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-670">
              <div className="p-6 rounded-xl bg-slate-50 border border-slate-200" data-id="element-671">
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-672">
                  ChatGPT
                </h3>
                <p className="text-slate-700" data-id="element-673">
                  Gives general advice — no real savings.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300" data-id="element-674">
                <h3 className="text-xl font-bold text-green-900 mb-3" data-id="element-675">
                  ShoppingExpert.ai
                </h3>
                <p className="text-green-900 font-semibold mb-3" data-id="element-676">
                  Saves 10%–40% through:
                </p>
                <ul className="space-y-2" data-id="element-677">
                  {['Coupon discovery', 'Deal timing', 'Price comparison', 'Avoiding "trap" models', 'Recommending better-value alternatives', 'Finding trustworthy sellers'].map((item, idx) => <li key={idx} className="flex items-center gap-2 text-slate-700" data-id="element-678">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0" data-id="element-679" />
                      <span data-id="element-680">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <p className="text-lg text-slate-700 leading-relaxed mt-6 font-semibold text-center" data-id="element-681">
              For real purchases, this is game-changing.
            </p>
          </div>

          {/* Multi-Channel Support */}
          <div className="mb-12" data-id="element-682">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-683">
              🛠 Multi-Channel Support
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-684">
              ShoppingExpert.ai works across:
            </p>
            <ul className="space-y-3 mb-6" data-id="element-685">
              {['Website', 'WhatsApp (coming soon)', 'ChatGPT apps', 'Future voice/phone assistant'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-lg text-slate-700" data-id="element-686">
                  <CheckCircle size={20} className="text-purple-600 flex-shrink-0" data-id="element-687" />
                  <span data-id="element-688">{item}</span>
                </li>)}
            </ul>
            <p className="text-slate-600 italic" data-id="element-689">
              ChatGPT is limited to its own interface.
            </p>
          </div>

          {/* Conclusion */}
          <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-id="element-690">
            <h2 className="text-3xl font-bold mb-6" data-id="element-691">
              🎯 Conclusion: Use ChatGPT for learning — use ShoppingExpert.ai
              for buying
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-6" data-id="element-692">
              <div data-id="element-693">
                <h3 className="text-xl font-bold mb-4 text-slate-300" data-id="element-694">
                  Use ChatGPT for:
                </h3>
                <ul className="space-y-2" data-id="element-695">
                  {['Ideas', 'Summaries', 'Opinions', 'Explanations'].map((item, idx) => <li key={idx} className="flex items-center gap-2" data-id="element-696">
                        <span className="text-slate-400" data-id="element-697">•</span>
                        <span data-id="element-698">{item}</span>
                      </li>)}
                </ul>
              </div>
              <div data-id="element-699">
                <h3 className="text-xl font-bold mb-4 text-purple-300" data-id="element-700">
                  Use ShoppingExpert.ai for:
                </h3>
                <ul className="space-y-2" data-id="element-701">
                  {['Buying electronics', 'Finding coupons', 'Comparing stores', 'Discovering real product problems', 'Planning your shopping strategy', 'Getting the best deal today'].map((item, idx) => <li key={idx} className="flex items-center gap-2" data-id="element-702">
                      <CheckCircle size={16} className="text-green-400 flex-shrink-0" data-id="element-703" />
                      <span data-id="element-704">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
            <p className="text-2xl font-bold text-center" data-id="element-705">
              ChatGPT = knowledge • ShoppingExpert.ai = decisions
            </p>
          </div>

          {/* Try It Yourself CTA */}
          <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 text-white" data-id="element-706">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3" data-id="element-707">
              <ShoppingBag size={32} data-id="element-708" />
              🛒 Try These Agents Now
            </h2>
            <p className="text-lg mb-6 opacity-90" data-id="element-709">
              Experience the difference:
            </p>
            <div className="space-y-3" data-id="element-710">
              <Link to="/?category=general" className="block w-full sm:w-auto px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-center" data-id="element-711">
                👉 Talk to the Electronics AI Expert
              </Link>
              <Link to="/?category=shopping" className="block w-full sm:w-auto px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-center" data-id="element-712">
                👉 Find hidden coupons with the AI Coupon Advisor
              </Link>
              <Link to="/?category=shopping" className="block w-full sm:w-auto px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-center" data-id="element-713">
                👉 Compare Amazon vs BestBuy with the Price Comparison Agent
              </Link>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-12" data-id="element-714">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-715">
              ❓ Frequently Asked Questions
            </h2>
            <div className="space-y-6" data-id="element-716">
              {[{
              q: 'Can ChatGPT give shopping advice?',
              a: 'Yes — but it lacks real-time data, price checks, and coupon analysis.'
            }, {
              q: 'Why is ShoppingExpert.ai more accurate?',
              a: 'Because it uses dedicated multi-agents trained specifically for the shopping domain.'
            }, {
              q: 'Does ShoppingExpert.ai work with Amazon?',
              a: 'Yes — it compares Amazon with other major U.S. retailers.'
            }, {
              q: 'Can I use it for fashion?',
              a: 'Yes — the fashion AI stylist analyzes fit, fabrics, style, and alternatives.'
            }, {
              q: 'Is it free?',
              a: 'Many features are free, with more advanced agents coming soon.'
            }].map((faq, idx) => <div key={idx} className="p-6 rounded-xl bg-slate-50 border border-slate-200" data-id="element-717">
                  <h3 className="font-bold text-lg text-slate-900 mb-2" data-id="element-718">
                    {faq.q}
                  </h3>
                  <p className="text-slate-700" data-id="element-719">{faq.a}</p>
                </div>)}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-id="element-720">
            <h2 className="text-2xl font-bold mb-4" data-id="element-721">
              📢 Ready to Make Smarter Buying Decisions?
            </h2>
            <p className="text-lg mb-6 opacity-90" data-id="element-722">
              Try the Electronics AI Expert and see why a specialized shopping
              assistant beats generic AI every time.
            </p>
            <Link to="/?category=general" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 rounded-xl font-bold text-lg hover:bg-slate-100 transition-colors" data-id="element-723">
              Get Started Free
              <ArrowRight size={20} data-id="element-724" />
            </Link>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12" data-id="element-725">
          <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-726">
            Related Articles
          </h3>
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-727">
            <Link to="/what-is-ai-shopping-assistant" className="p-6 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all" data-id="element-728">
              <h4 className="font-bold text-lg text-slate-900 mb-2" data-id="element-729">
                What Is an AI Shopping Assistant?
              </h4>
              <p className="text-slate-600 text-sm" data-id="element-730">
                Complete guide to AI shopping assistants in 2025
              </p>
            </Link>
            <Link to="/" className="p-6 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all" data-id="element-731">
              <h4 className="font-bold text-lg text-slate-900 mb-2" data-id="element-732">
                Explore All AI Shopping Tools
              </h4>
              <p className="text-slate-600 text-sm" data-id="element-733">
                Browse 100+ AI assistants across 20 categories
              </p>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16" data-id="element-734">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-id="element-735">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500" data-id="element-736">
            <div data-id="element-737">© 2025 ShoppingExpert.ai • All rights reserved</div>
            <div className="flex items-center gap-3" data-id="element-738">
              <Link to="/about" className="underline" data-id="element-739">
                About
              </Link>
              <Link to="/contact" className="underline" data-id="element-740">
                Contact
              </Link>
              <Link to="/" className="underline" data-id="element-741">
                Home
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}